import React, {useEffect, useState} from "react";
import axios from "axios";
import {Alert, Table} from "react-bootstrap";
import {FlexToast, useExternalControls} from "laravel-inertia-backend";
import AnalysisCard from "./AnalysisCard.jsx";
import HeaderWithHelp from "./HeaderWithHelp.jsx";
import SelectList from "../SelectList.jsx";
import {friendlyErrorMessage, hasAbility} from "./format.js";

const UNASSIGNED = "";

/**
 * Vertreterkürzel mit Zuordnung eines Ordermanagement-Benutzers
 * (Mindestteil der Stammdatenseite, plan.md AP6/AP13). Ehemalige Kürzel
 * sind gekennzeichnet, aber weiter zuordenbar (Historie bleibt sichtbar).
 * Die Zuordnung lässt sich nur mit dem Recht `abilities.assign` ändern -
 * ohne dieses Recht erscheint die Spalte als reiner Text; ein 403 beim
 * Speichern (z. B. bei fehlender Ability-Angabe) zeigt trotzdem den
 * Hinweis auf das benötigte Recht statt eines allgemeinen Fehlers.
 */
export default function StammdatenRepsTable({meta}) {
    const {addToast} = useExternalControls();
    const [reps, setReps] = useState(null);
    const [users, setUsers] = useState([]);
    const [error, setError] = useState(null);
    const [forbidden, setForbidden] = useState(false);
    const [savingCode, setSavingCode] = useState(null);

    const canAssign = hasAbility(meta, "assign");

    useEffect(() => {
        let active = true;
        Promise.all([axios.get("/vertrieb/daten/stammdaten/vertreter"), axios.get("/vertrieb/daten/benutzer")])
            .then(([repsRes, usersRes]) => {
                if (!active) {
                    return;
                }
                setReps(repsRes.data?.rows || repsRes.data || []);
                setUsers(usersRes.data?.rows || usersRes.data || []);
            })
            .catch((e) => {
                if (active) {
                    setError(friendlyErrorMessage(e, "Die Vertreterkürzel konnten nicht geladen werden."));
                }
            });
        return () => {
            active = false;
        };
    }, []);

    const userOptions = [{value: UNASSIGNED, label: "Nicht zugeordnet"}, ...users.map((u) => ({value: String(u.id), label: u.name}))];

    const saveUser = (code, userId) => {
        setSavingCode(code);
        axios
            .put(`/vertrieb/stammdaten/vertreter/${code}`, {user_id: userId || null})
            .then(() => {
                setReps((current) => current.map((rep) => (rep.code === code ? {...rep, user_id: userId || null} : rep)));
                addToast(<FlexToast variant="success" title="Vertriebsauswertung" autohide message={`Zuordnung für ${code} gespeichert.`} />);
            })
            .catch((e) => {
                if (e.response?.status === 403) {
                    setForbidden(true);
                } else {
                    addToast(
                        <FlexToast
                            variant="danger"
                            title="Vertriebsauswertung"
                            autohide={false}
                            message={friendlyErrorMessage(e, "Die Zuordnung konnte nicht gespeichert werden.")}
                        />
                    );
                }
            })
            .finally(() => setSavingCode(null));
    };

    return (
        <AnalysisCard
            title="Vertreterkürzel"
            help="Ordnet jedem Vertreterkürzel einen Ordermanagement-Benutzer zu; steuert, welche Kunden in wessen Vertretersicht erscheinen."
            loading={reps === null && !error}
            error={error}
            empty={reps !== null && reps.length === 0}
        >
            {forbidden && (
                <Alert variant="warning" className="mb-3">
                    Nur mit dem Recht „Vertriebsauswertung: Zuständigkeit und Stammdaten ändern" lässt sich die Zuordnung ändern.
                </Alert>
            )}
            <div className="table-responsive">
                <Table size="sm" hover className="mb-0 align-middle">
                    <thead>
                        <tr>
                            <th>
                                <HeaderWithHelp help="Vertreterkürzel aus der Vorlage bzw. den Altdaten.">Kürzel</HeaderWithHelp>
                            </th>
                            <th>
                                <HeaderWithHelp help="Name des Vertreters/Kürzels.">Name</HeaderWithHelp>
                            </th>
                            <th>
                                <HeaderWithHelp help="Aktiv oder ehemalig; ehemalige Kürzel bleiben für die Historie wählbar.">Status</HeaderWithHelp>
                            </th>
                            <th>
                                <HeaderWithHelp help="Ordermanagement-Benutzer, dem dieses Kürzel zugeordnet ist. Bestimmt, welcher Kunde in seiner Vertretersicht erscheint.">
                                    Benutzer
                                </HeaderWithHelp>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {(reps || []).map((rep) => (
                            <tr key={rep.code}>
                                <td>{rep.code}</td>
                                <td>{rep.name}</td>
                                <td>{rep.is_former ? "ehemalig" : "aktiv"}</td>
                                <td style={{maxWidth: 260}}>
                                    {canAssign ? (
                                        <SelectList
                                            size="sm"
                                            disabled={savingCode === rep.code}
                                            value={rep.user_id ? String(rep.user_id) : UNASSIGNED}
                                            options={userOptions}
                                            onChange={(value) => saveUser(rep.code, value)}
                                            ariaLabel={`Benutzer für ${rep.code}`}
                                        />
                                    ) : (
                                        rep.user?.name || "Nicht zugeordnet"
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </div>
        </AnalysisCard>
    );
}
